/* eslint-disable camelcase */
import { useState } from 'react';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch } from 'react-redux';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { isMobile } from 'react-device-detect';
import PropTypes from 'prop-types';
import { EditOutlined } from '@mui/icons-material';
import SelectDrillDetails from '../../../ui/SelectDrillDetails';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';
import Datagrid from '../../../ui/Datagrid';
import WellboreInitDialog from './WellboreInitDialog';
import WellboreTargetDialog from './WellboreTargetDialog';
import columns from './table_config';
import MobileWellboreRunInput from '../../../mobile/hydraulics/featuredWellRunInputs/wellboreRunInput';
import MobileSelectDrillDetails from '../../../mobile/hydraulics/SelectDrillDetails';
import { HIDE_TARGET_ECD } from '../../../../utils/constants';
import MobileWellboreTargetDialog from '../../../mobile/hydraulics/targetDialog';

const WellboreRunInputs = ({
  properties,
  setProperties,
  targetECD,
  anchorPoint,
  setAnchorPoint,
  setTargetECD,
  setTargetData,
  setWellbore,
  wellbore,
  setParameterDirty,
  checkWellbore,
  proxyLoader,
  hydraulicParameterData,
  setCheckWellbore,
  data,
  featuredWell,
  runType,
  hydraulicsRunUnits,
  targetData
}) => {
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [showWellboreInitModal, setShowWellboreInitModal] = useState(false);

  const handleAnchorPointTargetECD = (newAnchorPoint, newTargetECD) => {
    setAnchorPoint(newAnchorPoint);
    setTargetECD(newTargetECD);
  };

  const updateTargetData = (data) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === data?._id) {
          return {
            ...item,
            isDirty: true,
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const updatedItem = {
            ...item,
            [field]: value,
            isDirty: value !== item[field],
          };

          if (
            field === 'hole_depth' &&
            (item.bit_depth === '' || item.bit_depth === null || item.bit_depth === undefined || item.bit_depth === item.hole_depth)
          ) {
            updatedItem.bit_depth = value;
          }

          return updatedItem;
        }
        return item;
      });

      return updatedProperties;
    });
  };

  const renderDrillFluidCell = (params) => {
    if (isMobile) {
      return (
        <MobileSelectDrillDetails
          {...params}
          closeCellAfterSelect={true}
          updateData={updateData}
          data={data?.drillFluids}
          label={'ft'}
          decimals={2}
          loading={proxyLoader}
        />
      );
    }
    return (
      <SelectDrillDetails
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={data?.drillFluids}
        label={'ft'}
        decimals={2}
      />
    );
  };

  const renderTargetsEditAction = () => {
    return (
    <Grid container justifyContent="center" sx={{ opacity: proxyLoader ? 0.5 : 1 }}>
      <Grid item>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            disabled={proxyLoader}
            onClick={() => {
              setShowTargetModal(true);
              dispatch({
                type: REDUX_ACTIONS.OBSERVATION_LIST_LOADING,
              });
            }}
            sx={{
              p: { xs: 1.2, sm: 1 },
            }}
          >
            <EditOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>

          {HIDE_TARGET_ECD.includes(anchorPoint)
            ? (
            <Typography
              variant="button"
              fontSize={{ xs: '0.75rem', sm: '1rem' }}
              fontWeight={900}
            >
              None
            </Typography>
              )
            : (
            <Box display="flex" flexDirection="column">
              <Typography
                variant="button"
                fontSize={{ xs: '0.75rem', sm: '1rem' }}
                fontWeight={900}
                lineHeight={1.2}
              >
                {targetECD} ppg
              </Typography>
              <Typography
                variant="button"
                fontSize={{ xs: '0.75rem', sm: '1rem' }}
                fontWeight={900}
                lineHeight={1.2}
              >
                @ {anchorPoint}
              </Typography>
            </Box>
              )}
        </Box>
      </Grid>
    </Grid>
    );
  };

  const renderWellboreInitializationEditAction = () => {
    return (
    <Grid container justifyContent="center" sx={{ opacity: proxyLoader ? 0.5 : 1 }}>
      <Grid item>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            disabled={proxyLoader}
            onClick={() => {
              setShowWellboreInitModal(true);
            }}
            sx={{
              p: { xs: 1.2, sm: 1 },
            }}
          >
            <EditOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>

          <Typography
            variant="button"
            fontSize={{ xs: '0.75rem', sm: '1rem' }}
            fontWeight={900}
          >
            {((wellbore?.pipe.length || wellbore?.annular.length) && !checkWellbore) ? 'Applied' : 'None'}
          </Typography>
        </Box>
      </Grid>
    </Grid>
    );
  };

  return (
    <>
    <Grid container maxWidth='1000px'>
      <Grid item xs={12}>
          {isMobile
            ? (
            <MobileWellboreRunInput
              data={properties}
              columns={columns(renderDrillFluidCell, renderTargetsEditAction, renderWellboreInitializationEditAction, properties, hydraulicParameterData, runType)}
              updateData={updateData}
              loading={proxyLoader}
            />
              )
            : (
            <Datagrid
              apiRef={apiRef}
              data={properties || []}
              loading={proxyLoader}
              sx={{
                marginLeft: '-6px',
                '.MuiDataGrid-cell': {
                  fontSize: '1rem',
                  fontWeight: 900,
                  pointerEvents: proxyLoader ? 'none' : 'auto',
                  backgroundColor: proxyLoader ? '#f5f5f5' : 'inherit'
                },
                '.MuiDataGrid-columnHeader': {
                  fontSize: '1.2rem',
                  fontWeight: '900 !important'
                },
                '& .MuiDataGrid-footerContainer': {
                  display: 'none',
                },
              }}
              columns={columns(renderDrillFluidCell, renderTargetsEditAction, renderWellboreInitializationEditAction, properties, hydraulicParameterData, runType)}
              onRowClick={(params, evt) => {
                evt.preventDefault();
                evt.stopPropagation();
              }}
              disableStickyHeader={false}
              isRowSelectable={() => !proxyLoader}
              getRowClassName={() =>
                proxyLoader ? 'Mui-disabled' : ''
              }
              autoHeight={false}
              autoheight
              height={'150px'}
              pageSize={100}
              editMode="cell"
              toolbar={{
                options: {
                  columns: false,
                  filters: false,
                  density: false,
                  export: false
                },
              }}
            />
              )}
        </Grid>
    </Grid>
    {showTargetModal
      ? <>
      {isMobile
        ? (
        <MobileWellboreTargetDialog
         updateTargetData={updateTargetData}
              data={featuredWell?.run_details}
              anchorPoint={anchorPoint}
              targetECD={targetECD}
              setTargetData={setTargetData}
              isOpen={showTargetModal}
              setAnchorPoint={setAnchorPoint}
              setTargetECD={setTargetECD}
              onSave={handleAnchorPointTargetECD}
              onCloseClick={() => {
                setShowTargetModal(false);
              }}
              hydraulicsRunUnits={hydraulicsRunUnits}
              targetData={targetData}
        />
          )
        : (
        <WellboreTargetDialog
              updateTargetData={updateTargetData}
              data={featuredWell?.run_details}
              anchorPoint={anchorPoint}
              targetECD={targetECD}
              setTargetData={setTargetData}
              isOpen={showTargetModal}
              setAnchorPoint={setAnchorPoint}
              setTargetECD={setTargetECD}
              onSave={handleAnchorPointTargetECD}
              onCloseClick={() => {
                setShowTargetModal(false);
              }}
              hydraulicsRunUnits={hydraulicsRunUnits}
              targetData={targetData}
            />
          )}
      </>
      : null
          }
          {showWellboreInitModal
            ? <WellboreInitDialog
              isOpen={showWellboreInitModal}
              onCloseClick={() => {
                setShowWellboreInitModal(false);
              }}
              setWellbore={setWellbore}
              wellbore={wellbore}
              setParameterDirty={setParameterDirty}
              setCheckWellbore={setCheckWellbore}
              data={data}
              showVolumeCalculator={properties[0]?.hole_depth && properties[0]?.bit_depth}
              featuredWell={featuredWell}
              properties={properties}
            />
            : null
          }
        </>
  );
};
WellboreRunInputs.propTypes = {
  properties: PropTypes.array,
  setProperties: PropTypes.func,
  targetECD: PropTypes.any,
  anchorPoint: PropTypes.any,
  setAnchorPoint: PropTypes.func,
  setTargetECD: PropTypes.func,
  setTargetData: PropTypes.func,
  data: PropTypes.any,
  selectedJob: PropTypes.any,
  setWellbore: PropTypes.any,
  wellbore: PropTypes.any,
  setParameterDirty: PropTypes.any,
  checkWellbore: PropTypes.any,
  proxyLoader: PropTypes.any,
  hydraulicParameterData: PropTypes.any,
  setCheckWellbore: PropTypes.any,
  featuredWell: PropTypes.any,
  runType: PropTypes.any,
  hydraulicsRunUnits: PropTypes.any,
  targetData: PropTypes.any
};

export default WellboreRunInputs;
